import { debounce, isEqual } from 'lodash';
import React, { useCallback, useEffect, useRef } from 'react';
import { FieldValues } from 'react-hook-form';
import Bluebird from 'bluebird';
import { useQuestionnaireContext, useQuestionnaireFormContext } from '@/components/Questionnaire/QuestionnaireProvider';
import useNoodleApi from '@/hooks/useNoodleApi';
import runAiAgent from '@/tsClient/questionnaires/runAiAgent';
import { AiAgent } from '@/typings/questionnaires-api-models';
import removeNullish from '@/helpers/removeNullish';
import simpleDebug from '@/helpers/simpleDebug';
import parseBoxIndexRules from './boxIndexHelpers/parseBoxIndexRules';
import processWithRules from './boxIndexHelpers/processWithRules';
import parseFieldKey from './helpers/parseFieldKey';
import useInputFieldKeys from './helpers/useInputFieldKeys';
import useOutputFieldKeys from './helpers/useOutputFieldKeys';
import useWatchFields from '../../helpers/useWatchFields';
import getCountyFromAddress from './helpers/getCountyFromAddress';
import shouldSetValue from './helpers/shouldSetValue';
import getBoxIndexOutputFields from './boxIndexHelpers/getBoxIndexOutputFields';
import { FieldInputData, ParsedField } from './helpers/typings';
import Chunks, { ChunkSize, pickChunkSize } from './helpers/Chunks';

type Props = {
  key: string;
  aiAgent:
    & AiAgent
    & { // placeholder for making this settable
      chunkSize?: ChunkSize;
    }
  questionnaireId: string;
  onStartAgent: React.Dispatch<React.SetStateAction<string[]>>;
};

type Chunk = {
  numItems: number;
  fieldInputData: FieldInputData;
  fieldOutputs: ParsedField[];
};

type RunAgentProps = {
  agent: AiAgent;
  chunks: Chunk[];
  formValues: FieldValues;
};

const stateOfFilingFieldKey = 'districtOfState';
const whereDoYouLiveFieldKey = 'whereYouLive';
const omitForPdfFillsKey = 'mastercreditorlistfield59';

const HandleAiAgentAutofill: React.FC<Props> = ({ aiAgent, questionnaireId, onStartAgent }) => {
  const {
    fieldsByKey,
    userWorkflowId,
  } = useQuestionnaireContext();
  const {
    formMethods,
    getResponseSource,
    setValue,
  } = useQuestionnaireFormContext();

  const chunkSizeRef = useRef<ChunkSize>(1);
  chunkSizeRef.current = pickChunkSize(aiAgent.chunkSize);

  const inputFieldKeysMain = useInputFieldKeys({ aiAgent });
  const outputFieldKeysMain = useOutputFieldKeys({ aiAgent });
  const watchedFields = useWatchFields(inputFieldKeysMain);
  const previousRunValuesRef = useRef<Record<string, unknown> | null>(null);

  const { getData: runAiAgentFn } = useNoodleApi(runAiAgent);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- debounce...
  const debouncedRunAgent = useCallback<(args: RunAgentProps) => Promise<void> | void>(
    debounce(
      async ({
        agent,
        chunks,
        formValues,
      }: RunAgentProps): Promise<void> => {
        const chunksToRun = chunks.filter((chunk) => Object.keys(chunk.fieldInputData).length > 0);
        chunksToRun.forEach((chunk) => {
          const outputFieldKeys = chunk.fieldOutputs.map((of) => of.fullFieldKey);
          onStartAgent((prev) => [...prev, ...outputFieldKeys]);
        });
        await Bluebird.map(
          chunks,
          async (chunk) => {
            const { fieldInputData, fieldOutputs } = chunk;
            const outputFieldKeys = chunk.fieldOutputs.map((of) => of.fullFieldKey);
            if(agent.aiModel==='omitForPdfFills') {
              fieldOutputs?.forEach((outputField) => {
                const masterFieldKey = outputField.listFieldKey;
                const indexNumber = outputField.rowIndex;

                if(masterFieldKey && typeof indexNumber === 'number' && indexNumber>=0) {
                  let globalDecider = false;
                  agent?.fieldData?.forEach((f) => {
                    const fetchedValue = formValues[masterFieldKey][indexNumber][f];
                    const decider = (typeof fetchedValue === 'string' && fetchedValue==='closed') || (typeof fetchedValue === 'object' && (fetchedValue?.amount==='NaN' || Number(fetchedValue?.amount)<=0));
                    if(decider) {
                      globalDecider = true;
                    }
                  });

                  setValue({
                    key:`${masterFieldKey}[${indexNumber}].${omitForPdfFillsKey}`,
                    value: globalDecider ? ['omit'] : [],
                  });
                }
              });
            }
            else if (agent.aiModel === 'boxIndex') {
              const rules = parseBoxIndexRules(aiAgent.instructions);
              fieldOutputs.forEach((outputField) => {
                const masterFieldKey = outputField.listFieldKey;
                const indexNumber = outputField.rowIndex;
                if (indexNumber !== null && masterFieldKey !== null) {
                  getBoxIndexOutputFields(outputField, formValues).forEach((fieldToUpdate) => {
                    const value = processWithRules({ formValues, indexNumber: fieldToUpdate.index, masterFieldKey, rules });
                    if (value !== undefined) {
                      setValue({ key: fieldToUpdate.key, source: 'ai', value });
                    }
                  });
                }
              });
            } else if (agent.aiModel === 'googlePlaces') {
              await Bluebird.map(
                Object.keys(fieldInputData),
                async (inputFieldKey) => {
                  const data = fieldInputData[inputFieldKey];
                  const usAddress = data.value ? data.value as unknown : null;
                  const county = await getCountyFromAddress(usAddress);

                  if (!county) {
                    return;
                  }

                  const parsedInputField = parseFieldKey(inputFieldKey);

                  fieldOutputs.forEach((outputField) => {
                    if (shouldSetValue({ inputField: parsedInputField, outputField })) {
                      setValue({ key: outputField.fullFieldKey, source: 'ai', value: county });
                    }
                  });
                },
              );
            } else {
              let metaInput={};
              if(aiAgent.slug === "get-filing-district") {
                const fieldKey = aiAgent.fieldData?.[0];
                const zipcode = formValues[whereDoYouLiveFieldKey]?.[0]?.[fieldKey]?.zip;
                if(zipcode) {
                  metaInput={
                    ...metaInput,
                    [fieldKey]:{
                      label:'Zipcode',
                      value:zipcode,
                    },
                  };
                }
              }
              const stateOfFiling = formMethods.getValues()[stateOfFilingFieldKey] as string | null;
              const result = await runAiAgentFn({
                agentId: agent.id,
                creatorId: agent.creatorId,
                fieldInputData:{
                  ...fieldInputData,
                  ...metaInput,
                },
                questionnaireId,
                questionnaireTemplateId: agent.questionnaireTemplateId,
                rowIndiciesToRun: fieldOutputs.map((of) => of.rowIndex).filter(removeNullish),
                stateOfFiling,
                userWorkflowId,
              });

              const aiAgentAnswers = result.data;

              if (aiAgentAnswers) {
                Object.entries(aiAgentAnswers).forEach(([key, value]) => {
                  if (getResponseSource(key) !== 'manual' && key !== 'aiRequestId') {
                    const splitKey = key.split('].');
                    let keyBuilt = key;
                    if(splitKey.length > 1) {
                      [,keyBuilt] = splitKey;
                    }
                    setValue({ aiRequestId: aiAgentAnswers?.aiRequestId, key, source: 'ai', value});
                    if(key === keyBuilt) {
                      const fieldsFetched = fieldsByKey[keyBuilt];
                      fieldsFetched?.linkedFieldKeys?.forEach((linkedFieldKey) => {
                        setValue({ aiRequestId: aiAgentAnswers?.aiRequestId, key:linkedFieldKey, source: 'ai', value});
                      });
                    }
                  }
                });
              }
            }

            onStartAgent((prev) => prev.filter((field) => !outputFieldKeys.includes(field)));
          },
          { concurrency: 3 },
        );
      },
      3000,
    ),
  [],
  );

  useEffect(() => {
    // @TODO: figure out how to debounce this.
    simpleDebug(aiAgent.slug, 'in useEffect', {
      fieldData: aiAgent.fieldData,
      inputFieldKeysMain,
      outputFieldKeysMain,
      previousRunValues: previousRunValuesRef.current,
      watchedFields,
    });
    if (
      !aiAgent.fieldData
      || !watchedFields
      || outputFieldKeysMain.length === 0
    ) return;

    const previousRunValues = previousRunValuesRef.current;

    // Set the previous values so that we can see if they change next time.
    // Set to null to start so that we can detect first load.
    previousRunValuesRef.current = watchedFields;

    if (previousRunValues && isEqual(previousRunValues, watchedFields)) {
      return;
    }

    const formValues = formMethods.getValues();

    const chunkHandler = new Chunks({
      aiAgent,
      fieldsByKey,
      formValues,
      getResponseSource,
      inputFieldKeys: inputFieldKeysMain,
      outputFieldKeys: outputFieldKeysMain,
      size: chunkSizeRef.current,
    });

    // First find any unset output values and see if we can run the agent on those
    chunkHandler.addUnsetOutputFields();

    // Then look at all input field values and check if any of them have changed
    // If so, and all of the inputs are set for that input, run the agent on that row.
    // But only if this is not on the initial mount.
    if (previousRunValues) {
      chunkHandler.handleWatchedFields({
        watchedFields,
        watchedFieldsPrevious: previousRunValues,
      });
    }

    simpleDebug(aiAgent.slug, `Final chunks`, {
      chunks: chunkHandler.chunks,
    });

    if (chunkHandler.chunks.length > 0) {
      previousRunValuesRef.current = watchedFields;
      debouncedRunAgent({
        agent: aiAgent,
        chunks: chunkHandler.chunks,
        formValues,
      });
    }
  }, [
    aiAgent,
    watchedFields,
    debouncedRunAgent,
    outputFieldKeysMain,
    inputFieldKeysMain,
    formMethods,
    getResponseSource,
    fieldsByKey,
  ]);

  return null;
};

export default HandleAiAgentAutofill;
